
import Vue from "vue";
import Truncate from "@/components/Truncate.vue";
import {buildContactFullName, Contact, contactApi, ContactSearchRow,} from "@/api/contacts";
import {ValidationProvider} from "vee-validate";
import {Encounter, encounterApi, EncounterType} from "../../api/encounter";
import axios from "axios";
import {flameApi} from "@/api/flame";
import DossierForm, {DossierFormInputDTO,} from "@/views/flames/dossier/DossierForm.vue";
import {mailboxApi} from "@/api/mailbox";
import DateTextFieldPicker from "@/components/DateTextFieldPicker.vue";
import AvatarMixin from "@/mixins/AvatarMixin";
import store from "@/store";
import AudioRecorder from "@/components/AudioRecorder.vue";
import QuickContactSearch from "@/views/contacts/QuickContactSearch.vue";
import EventBus from "@/utils/EventBus";
import {toLocalDateString} from "@/utils/UtcToDate";
import {LoadingStateEvent, SendMailToEvent} from "@/layout/Layout.vue";
import {findConfirmDialogRef} from "@/utils/RefUtils";

const calculateMonthsBetweenDates = (minDate: Date, maxDate: Date): number => {
  const yearDiff = maxDate.getFullYear() - minDate.getFullYear();
  const monthDiff = maxDate.getMonth() - minDate.getMonth();
  return yearDiff * 12 + monthDiff;
};


export interface EncounterFormDTO {
  contact: Contact;
  encounterRef: string | null;
}

// eslint-disable-next-line
export default Vue.extend({
  name: "MyEncounter",
  mixins: [AvatarMixin],
  props: {
    encounterForm: {
      type: Object as () => EncounterFormDTO,
      required: true,
    },
    small: {
      type: Boolean,
      required: false,
      default: false,
    },
    backgroundColor: {
      type: String,
      default: "#737778",
    }
  },
  components: {
    AudioRecorder,
    DateTextFieldPicker,
    DossierForm,
    ValidationProvider,
    Truncate,
    QuickContactSearch,
  },
  data() {
    return {
      searchTerm: "",
      searchTimeRange: [0, 1],
      searchTimeRangeMax: 1,
      searchTimeRangeTickSize: 1,
      searchContactDialog: false,
      mailboxId: "",
      messageContent: "",
      showSendMessageDialog: false,
      messageTo: [] as string[],
      encounters: [] as Encounter[],
      encounterDialog: false,
      encounterVoiceNoteDialog: false,
      isWorking: false,
      title: "",
      encounterType: "",
      searchEncounterType: "",
      description: "",
      createdAtDate: new Date(),
      createdAtTime: "",
      id: "",
      recordedBlob: null as Blob | null,
      audioFileDownload: new Map<string, boolean>(),
      showDossierForm: false,
      dossierFormInput: {} as DossierFormInputDTO,
      timePickerMenu: false,
      isAdmin: store.state.auth.isAdmin,
      currentEncounter: null as Encounter | null,
      start_date: new Date(),
      contentMailingsDdj: false,
      contentMailingsSeedsMagazine: false,
      contentMailingsPostcard: false,
      selectedEncounters: [] as Encounter[],
    };
  },
  computed: {
    currentEncounterTypeAsIcon(): string {
      const typedEncounterTypeString = this
          .encounterType as keyof typeof EncounterType;
      return this.mapEncounterTypToIcon(
          EncounterType[typedEncounterTypeString]
      );
    },
    startSearchDateStr(): string {
      return this.searchTimeRangeLabel(this.searchTimeRange[0], false);
    },
    endSearchDateStr(): string {
      return this.searchTimeRangeLabel(this.searchTimeRange[1], true);
    },
    encounterTypes(): { value: string; text: string }[] {
      return Object.keys(EncounterType)
          .filter(
              (k) =>
                  k !== EncounterType.FAX &&
                  k !== EncounterType.EMAIL_INCOMING &&
                  k !== EncounterType.EMAIL_OUTGOING &&
                  k !== EncounterType.VOICE_MEMO
          )
          .map((k) => ({
            value: k,
            text: this.$t(`enum.encounterType.${k}`) as string,
          }));
    },
    encounterTypesWithEmpty(): { value: string; text: string }[] {
      const encounters = Object.keys(EncounterType)
          .filter(
              (k) =>
                  k !== EncounterType.FAX &&
                  k !== EncounterType.EMAIL_INCOMING &&
                  k !== EncounterType.EMAIL_OUTGOING &&
                  k !== EncounterType.VOICE_MEMO
          )
          .map((k) => ({
            value: k,
            text: this.$t(`enum.encounterType.${k}`) as string,
          }));
      encounters.push({
        value: "",
        text: "-"
      })
      return encounters;
    },
  },
  watch: {
    encounterForm: {
      async handler() {
        await this.initialized();
      },
      immediate: true
    },
  },
  async mounted() {
    EventBus.$on("emailWasSend", this.initialized);
  },
  beforeDestroy() {
    EventBus.$off("emailWasSend");
  },
  methods: {
    isEncounterSelected(item: Encounter) {
      return this.selectedEncounters.includes(item);
    },
    selectAllEncounters() {
      if (this.selectedEncounters.length > 0) {
        this.selectedEncounters = []
      } else {
        this.encounters.forEach((encounter) => {
          this.selectedEncounters.push(encounter);
        })
      }
    },
    async transcribeAgain(encounter: Encounter) {
      const res = await (findConfirmDialogRef(this) as any).open(
          this.$t("Achtung") as string,
          this.$t(
              `Wirklich die Sprachnotiz erneut transkribieren? Dieser Vorgang wird im Hintergrund durchgeführt und kann einige Minuten dauern.`
          ) as string,
          {
            textColor: "red",
            noconfirm: false,
            secondOkButton: null,
          }
      );
      if (res) {
        encounterApi.transcribeAgain(encounter);
      }
    },
    findAssigneeRef(encounter: Encounter) {
      if (encounter.assigneeRef && encounter.assigneeRef.length > 0) {
        return encounter.assigneeRef;
      }
      if (encounter.updatedBy && encounter.updatedBy.length > 0) {
        return encounter.updatedBy;
      }
      if (encounter.createdBy && encounter.createdBy.length > 0) {
        return encounter.createdBy;
      }
      return null;
    },
    mailPossible(encounter: Encounter) {
      if (
          encounter.encounterType === EncounterType.EMAIL_OUTGOING ||
          encounter.encounterType === EncounterType.EMAIL_INCOMING
      ) {
        return true;
      }
      return false;
    },
    closeEncounterDialog() {
      this.resetValues();
      this.encounterDialog = false;
    },
    async changeTimeLine() {
      await this.search();
    },
    async startEncounterMoving(encounter: Encounter) {
      this.currentEncounter = encounter;
      this.searchContactDialog = true;
    },
    async moveEncounter(contactSearchRow: ContactSearchRow) {
      if (
          this.encounterForm.contact.id &&
          contactSearchRow.id &&
          (this.currentEncounter &&
              this.currentEncounter.id || this.selectedEncounters.length > 0)
      ) {
        const res = await (findConfirmDialogRef(this) as any).open(
            this.$t("Achtung") as string,
            this.$t(
                `Wirklich die Begegnung(en) zu dem ausgewählten Kontakt ${buildContactFullName(
                    contactSearchRow,
                    null
                )} bewegen?`
            ) as string,
            {
              textColor: "red",
              noconfirm: false,
              secondOkButton: null,
            }
        );
        if (res) {
          if (this.selectedEncounters.length > 0) {
            for (const encounter of this.selectedEncounters) {
              if (encounter.id) {
                await encounterApi.move(
                    this.encounterForm.contact.id,
                    encounter,
                    contactSearchRow.id
                );
              }
            }
          } else if (this.currentEncounter != null) {
            await encounterApi.move(
                this.encounterForm.contact.id,
                this.currentEncounter,
                contactSearchRow.id
            );
          }
        }
        this.currentEncounter = null;
        this.selectedEncounters = []
      }
      this.searchContactDialog = false;
      await this.initialized();
    },
    sendMailTo(encounter: Encounter, to: string) {
      EventBus.$emit("sendMailTo", {
        to: [to],
        contact: this.encounterForm.contact,
        prefilledContent: encounter.description ?? "",
      } as SendMailToEvent);
    },
    sendMailToAll(encounter: Encounter) {
      EventBus.$emit("sendMailTo", {
        to: this.encounterForm.contact.emailAddresses.map((e) => e.address),
        contact: this.encounterForm.contact,
        prefilledContent: encounter.description ?? "",
        cc: encounter.furtherEmailAddressesToAnswer.filter(
            (s) => s.length > 3 && s.indexOf("@") > 0
        ),
      } as SendMailToEvent);
    },
    async showDossierDetail(encounter: Encounter) {
      let taskId = encounter.upstreamId ?? "";
      let contact: Contact | null = null;
      if (encounter.duplicate && taskId.indexOf("#") > -1) {
        taskId =
            (await encounterApi.getById(taskId.split("#")[1])).upstreamId ?? "";
        contact = await contactApi.getById(
            encounter.upstreamId!.split("#")[0]
        );
      } else {
        contact = this.encounterForm.contact;
      }
      const flame = await flameApi.getById(taskId);
      this.dossierFormInput = {
        contact: contact,
        dossier: flame?.dossier ?? null,
        taskId: taskId,
        blackBoardRef: flame?.blackBoardRef ?? null,
      };
      this.showDossierForm = true;
    },
    downloadVoiceMemo(encounter: Encounter) {
      if (!this.audioFileDownload.get(encounter.id ?? "")) {
        axios({
          url: encounterApi.generateDownloadLink(
              this.encounterForm.contact.id ?? "",
              encounter.id ?? ""
          ),
          method: "GET",
          responseType: "blob",
        }).then((response) => {
          // eslint-disable-next-line
          (this.$refs["audioPlayerControl" + encounter.id] as any)[0].src = window.URL.createObjectURL(new Blob([response.data], {type: "audio/mp3"}));
          this.audioFileDownload.set(encounter.id ?? "", true);
          // eslint-disable-next-line
          (this.$refs["audioPlayerControl" + encounter.id] as any)[0].pause();
          // eslint-disable-next-line
          (this.$refs["audioPlayerControl" + encounter.id] as any)[0].load();
        });
      }
    },
    async uploadVoice() {
      try {
        if (this.recordedBlob) {
          await encounterApi.addVoiceMemo(this.encounterForm.contact.id ?? "", this.recordedBlob);
          this.recordedBlob = null;
          // eslint-disable-next-line
          const recorder = this.$refs.audioRecorder as any;
          if (recorder && recorder.reset) {
            recorder.reset();
          }
        } else {
          // eslint-disable-next-line
          await (findConfirmDialogRef(this) as any).open(
              this.$t("Achtung") as string,
              this.$t("Keine Aufnahme gefunden!") as string,
              {
                textColor: "red",
                secondOkButton: false,
                noconfirm: true,
                textOk: "Ok",

              }
          );
        }
      } finally {
        this.encounterVoiceNoteDialog = false;
        await this.initialized();
      }
    },
    onRecorded(blob: Blob) {
      this.recordedBlob = blob;
    },
    openNewContactVoiceNote() {
      this.encounterVoiceNoteDialog = true;
    },
    formatTitle(encounter: Encounter) {
      if (!encounter) {
        return this.$t("Unbekannt");
      }
      let titlePrefix = this.$t(
          `enum.encounterType.${encounter.encounterType}`
      );
      if (
          (encounter.encounterType === EncounterType.TASK ||
              encounter.encounterType === EncounterType.TODO ||
              encounter.encounterType === EncounterType.EMAIL_OUTGOING ||
              encounter.encounterType === EncounterType.EMAIL_INCOMING) &&
          encounter.title
      ) {
        titlePrefix = encounter.title;
      }

      return titlePrefix;
    },
    formatTitleShort(encounter: Encounter) {
      const convertToLocalDate = encounter.originalKnownSince;
      const [hours, minutes] = convertToLocalDate
          .toLocaleTimeString()
          .substr(0, 5)
          .split(":");
      return (
          convertToLocalDate.toLocaleDateString() +
          " um " +
          hours +
          ":" +
          minutes +
          " Uhr"
      );
    },
    openNewEncounter() {
      const [year, month, day] = new Date()
          .toISOString()
          .substr(0, 10)
          .split("-");
      this.title = "";
      this.encounterType = EncounterType.TELEPHONE.toString();
      this.description = "";
      this.createdAtDate = new Date();
      this.createdAtTime =
          new Date().getHours() + ":" + new Date().getMinutes();
      this.id = "";
      this.encounterDialog = true;
    },
    editEncounter(encounter: Encounter) {
      this.title = encounter.title ?? "";
      this.id = encounter.id ?? "";
      const convertToLocalDate = encounter.originalKnownSince;
      this.createdAtDate = encounter.originalKnownSince;
      const [hours, minutes] = convertToLocalDate
          .toTimeString()
          .substr(0, 5)
          .split(":");
      this.createdAtTime = hours + ":" + minutes;
      this.description = encounter.description ?? "";
      this.encounterType = encounter.encounterType;
      this.encounterDialog = true;
      this.contentMailingsDdj = encounter.contentMailingsDdj;
      this.contentMailingsSeedsMagazine =
          encounter.contentMailingsSeedsMagazine;
      this.contentMailingsPostcard = encounter.contentMailingsPostcard;
    },
    async removeEncounter(encounter: Encounter) {
      // eslint-disable-next-line
      const res = await (findConfirmDialogRef(this) as any).open(
          this.$t("Achtung") as string,
          this.$t("Wirklich den Eintrag für die Begegnung entfernen?") as string,
          {
            textColor: "red",
            textOk: "Ja",
            textCancel: "Nein und zurück",
            noconfirm: false,
            secondOkButton: false,
          }
      );
      if (res && encounter && encounter.id) {
        await encounterApi.delete(
            this.encounterForm.contact.id ?? "",
            encounter.id
        );
      }
      await this.initialized();
    },
    mapToStyle(encounter: Encounter) {
      switch (encounter.encounterType) {
        case EncounterType.EMAIL_INCOMING:
          return "color: #282d2f";
        default:
          return "color: #ab8661";
      }
    },
    mapToColor(encounter: Encounter) {
      if (encounter.duplicate) {
        return "rgba(155,48,48,0.8)";
      }
      switch (encounter.encounterType) {
        case EncounterType.EMAIL_INCOMING:
          return "#ab8661";
        default:
          return "primary";
      }
    },
    mapToIconColor(encounter: Encounter) {
      switch (encounter.encounterType) {
        case EncounterType.EMAIL_INCOMING:
          return "primary";
        default:
          return "#ab8661";
      }
    },
    selectEncounter(item: Encounter) {
      if (this.selectedEncounters.find((e) => e.id === item.id)) {
        this.selectedEncounters.splice(
            this.selectedEncounters.findIndex((e) => e.id === item.id),
            1
        );
      } else {
        this.selectedEncounters.push(item);
      }
    },
    mapEncounterTypToIcon(encounterType: EncounterType) {
      switch (encounterType) {
        case EncounterType.EMAIL_OUTGOING:
          return "mdi-email";
        case EncounterType.EMAIL_INCOMING:
          return "mdi-email";
        case EncounterType.FACE_TO_FACE:
          return "mdi-account";
        case EncounterType.FAX:
          return "mdi-fax";
        case EncounterType.TELEPHONE:
          return "mdi-phone";
        case EncounterType.VOICE_MEMO:
          return "mdi-microphone";
        case EncounterType.TASK:
          return "mdi-fire";
        case EncounterType.APPOINTMENT:
          return "mdi-calendar-range";
        case EncounterType.POST:
          return "mdi-postage-stamp";
        case EncounterType.TODO:
          return "mdi-format-list-checks";
        default:
          return "mdi-email";
      }
    },
    mapToIcon(encounter: Encounter) {
      if (!encounter) {
        return "mdi-email";
      }
      if (encounter.duplicate) {
        return "mdi-relation-many-to-many";
      }
      return this.mapEncounterTypToIcon(encounter.encounterType);
    },
    async addEncounter() {
      this.encounterDialog = false;
      let newCreatedAt = new Date(this.createdAtDate);
      if( this.createdAtTime ) {
        newCreatedAt.setHours(
          parseInt(this.createdAtTime.split(":")[0]),
          parseInt(this.createdAtTime.split(":")[1])
        )
      }
      newCreatedAt = new Date(newCreatedAt.toUTCString());
      const encounter = {
        id: this.id,
        title: this.title,
        originalKnownSince: newCreatedAt,
        description: this.description,
        encounterType: this.encounterType,
        contentMailingsDdj: this.contentMailingsDdj,
        contentMailingsPostcard: this.contentMailingsPostcard,
        contentMailingsSeedsMagazine: this.contentMailingsSeedsMagazine,
      } as Encounter;

      if (this.id) {
        await encounterApi.edit(
            this.encounterForm.contact.id ?? "",
            this.id,
            encounter
        );
      } else {
        await encounterApi.add(this.encounterForm.contact.id ?? "", encounter);
      }
      await this.initialized();
      this.$forceUpdate();
    },
    resetValues() {
      this.contentMailingsDdj = false;
      this.contentMailingsPostcard = false;
      this.contentMailingsSeedsMagazine = false;
      this.encounters = [];
    },
    async fillSlider(contactId: string) {
      const foundMinDateForEncounter = await encounterApi.getMinDate(contactId)
      const foundMaxDateForEncounter = await encounterApi.getMaxDate(contactId)
      if( foundMinDateForEncounter && foundMaxDateForEncounter ) {
        this.start_date = foundMinDateForEncounter
        this.searchTimeRangeMax =  calculateMonthsBetweenDates(foundMinDateForEncounter, foundMaxDateForEncounter);
        this.searchTimeRange = [0, this.searchTimeRangeMax];
      }
    },
    async initialized() {
      this.resetValues();
      if (this.encounterForm === null || !this.encounterForm.contact || !this.encounterForm.contact.id) {
        return;
      }
      await this.fillSlider(this.encounterForm.contact.id);
      if( this.searchTimeRangeMax >= 12) {
        this.searchTimeRange = [this.searchTimeRangeMax - 12, this.searchTimeRangeMax];
      }

      await this.search()
      const mailboxes = await mailboxApi.list();
      if (mailboxes && mailboxes.length > 0) {
        this.mailboxId = mailboxes[0].id ?? "";
      }
      if (
          this.encounterForm.encounterRef &&
          this.encounterForm.encounterRef !== "-1"
      ) {
        this.encounters = this.encounters.filter(
            (e) => e.id === this.encounterForm.encounterRef
        );
      }
      if (
          this.encounterForm.encounterRef &&
          this.encounterForm.encounterRef === "-1"
      ) {
        this.openNewEncounter();
      }
    },
    async removeSelectedEncounters() {
      const res = await (findConfirmDialogRef(this) as any).open(
          this.$t("Achtung") as string,
          this.$t("Wirklich alle selektierten Begegnungen entfernen?") as string,
          {
            textColor: "red",
            textOk: "Ja",
            textCancel: "Nein und zurück",
            noconfirm: false,
            secondOkButton: false,
          }
      );
      if (res && this.selectedEncounters.length > 0) {
        for (const encounter of this.selectedEncounters) {
          if (encounter.id) {
            await encounterApi.delete(
                this.encounterForm.contact.id ?? "",
                encounter.id
            );
          }
        }
        this.selectedEncounters = []
      }
      await this.initialized();
    },
    async moveSelectedEncounters() {
      const res = await (findConfirmDialogRef(this) as any).open(
          this.$t("Achtung") as string,
          this.$t("Wirklich alle selektierten Begegnungen bewegen?") as string,
          {
            textColor: "red",
            textOk: "Ja",
            textCancel: "Nein und zurück",
            noconfirm: false,
            secondOkButton: false,
          }
      );
      if (res && this.selectedEncounters.length > 0) {
        this.searchContactDialog = true
      }
    },
    async search() {
      if (this.encounterForm.contact.id) {
        EventBus.$emit("loading", {loading: true} as LoadingStateEvent);
        const [startDay, startMonth, startYear] =
            this.startSearchDateStr.indexOf("/") > -1
                ? this.startSearchDateStr.split("/")
                : this.startSearchDateStr.split(".");
        const [endDay, endMonth, endYear] =
            this.endSearchDateStr.indexOf("/") > -1
                ? this.endSearchDateStr.split("/")
                : this.endSearchDateStr.split(".");
        this.encounters = (
            await encounterApi.search(
                this.encounterForm.contact.id,
                this.searchTerm,
                `${startYear}-${startMonth.padStart(2, "0")}-${startDay.padStart(
                    2,
                    "0"
                )}`,
                `${endYear}-${endMonth.padStart(2, "0")}-${endDay.padStart(2, "0")}`
                ,
                this.searchEncounterType
            )
        ).sort(
            (a, b) =>
                b.originalKnownSince.getTime() - a.originalKnownSince.getTime()
        );
        EventBus.$emit("loading", {loading: false} as LoadingStateEvent);
      }
    },
    searchTimeRangeLabel(val: number, lastDay: boolean) {
      let n = new Date();
      n.setDate(1);
      n.setFullYear(this.start_date.getFullYear());
      n.setMonth(this.start_date.getMonth() + val);
      if (lastDay) {
        n.setDate(new Date(n.getFullYear(), n.getMonth() + 1, 0).getDate());
      }
      return toLocalDateString(n);
    },
  },
});
